import type { CreateTRPCReact } from '@trpc/react-query'
import { createTRPCReact } from '@trpc/react-query'
import { httpBatchLink, splitLink } from '@trpc/client'

import type { AppRouter } from '@api/trpc/routers'

import { getSessionAuthority } from './sessionAuthority'
import type { AdvanceSessionEpoch } from './sessionAuthority'

export type { AppRouter }
export const trpc: CreateTRPCReact<AppRouter, unknown> = createTRPCReact<AppRouter>()
const sessionChanges = new Set([
  'auth.login', 'auth.magicLogin', 'auth.setupAccount', 'auth.linkAccount', 'auth.logout',
])

function getBaseUrl(): string {
  if (import.meta.env.VITE_API_BASE_URL) return import.meta.env.VITE_API_BASE_URL
  return 'https://api.badland.ai'
}

function responseHasUnauthorizedError(value: unknown): boolean {
  const responses = Array.isArray(value) ? value : [value]
  return responses.some((response) => {
    if (typeof response !== 'object' || response === null || !('error' in response)) return false
    const error = response.error
    if (typeof error !== 'object' || error === null || !('data' in error)) return false
    const data = error.data
    return typeof data === 'object' && data !== null && 'code' in data && data.code === 'UNAUTHORIZED'
  })
}

export function createTrpcClient() {
  const baseUrl = getBaseUrl()
  const authority = getSessionAuthority(baseUrl)

  // The server sees HttpOnly and legacy cookies. Only its explicit negative
  // signal rules out refresh; old responses and outages keep recovery intact.
  async function serverRulesOutRefresh(): Promise<boolean> {
    try {
      const status = await fetch(`${baseUrl}/api/auth/status`, { credentials: 'include' })
      if (!status.ok) return false
      const body = await status.json() as { success?: boolean; data?: { refreshable?: boolean } }
      return body.success === true && body.data?.refreshable === false
    } catch {
      // A failed status check is not proof that the session is gone.
      return false
    }
  }

  async function refreshSession(epoch: string | null): Promise<boolean> {
    if (epoch === null || !authority.isCurrent(epoch)) return false
    return authority.refresh(epoch, () => authority.write(async () => {
      // A different tab can change accounts while this refresh waits for its lock.
      if (!authority.isCurrent(epoch)) return false
      if (await serverRulesOutRefresh()) return false
      const response = await fetch(`${baseUrl}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      })
      return response.ok
    }).catch(() => false))
  }

  function createTransport(maxItems?: number) {
    return httpBatchLink<AppRouter>({
      url: `${baseUrl}/trpc`,
      maxURLLength: 2083,
      maxItems,
      async fetch(url, options) {
        const requestOptions = { ...options, credentials: 'include' as const }
        const requestUrl = typeof url === 'string'
          ? url
          : url instanceof URL
            ? url.href
            : url.url
        const procedures = new URL(requestUrl).pathname.split('/').slice(-1)[0]?.split(',') ?? []
        const changesSession = procedures.some(path => sessionChanges.has(path))
        let requestEpoch = await authority.snapshot()
        async function sendRequest(advanceEpoch?: AdvanceSessionEpoch): Promise<Response> {
          if (changesSession && advanceEpoch) requestEpoch = advanceEpoch('pending')
          let receivedResponse: Response | undefined
          return fetch(url, requestOptions).then(response => {
            receivedResponse = response
            return response
          }).finally(() => {
            if (changesSession && advanceEpoch) requestEpoch = advanceEpoch()
          }).catch(error => {
            requestEpoch = null
            // Storage failure cannot undo cookies already set by the server.
            if (receivedResponse) return receivedResponse
            throw error
          })
        }
        // Cookie writers run in order in either direction: refresh can also
        // start while a login is in flight. Ordinary RPCs remain concurrent.
        const writesCookies = changesSession || procedures.includes('auth.refresh')
        const execute = (retryResponse?: Response) => {
          const send = (advanceEpoch?: AdvanceSessionEpoch) => {
            // Another cookie writer can finish while this retry is queued.
            if (retryResponse && !authority.isCurrent(requestEpoch)) return Promise.resolve(retryResponse)
            return sendRequest(advanceEpoch)
          }
          return writesCookies || retryResponse ? authority.write(send, options?.signal) : send()
        }
        const response = await execute()
        if (procedures.includes('auth.refresh')) return response

        let unauthorized = response.status === 401
        if (!unauthorized) {
          try {
            unauthorized = responseHasUnauthorizedError(await response.clone().json())
          } catch {
            // Non-JSON responses are handled by tRPC normally.
          }
        }

        // Never replay an outgoing account's payload under new credentials.
        if (unauthorized && authority.isCurrent(requestEpoch)
          && await refreshSession(requestEpoch) && authority.isCurrent(requestEpoch)) {
          return execute(response)
        }
        return response
      },
    })
  }

  return trpc.createClient({
    links: [
      splitLink({
        // A batched server executes mutations concurrently within one response;
        // each cookie writer needs its own queue admission and settlement.
        condition: operation => sessionChanges.has(operation.path) || operation.path === 'auth.refresh',
        true: createTransport(1),
        false: createTransport(),
      }),
    ],
  })
}
